import axios, { RawAxiosRequestHeaders } from "axios";
import Cookies from "universal-cookie";

import {
  ApiCampaignResponse,
  ApiHeaders,
  LeaderboardCampaignResponse,
  LeaderboardResponse,
  UserAuth,
  UserDetails,
} from "./leaderBoardTypes";
import General from "../../../../general";
import config from "../../../../config";

class LeaderBoardApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL =
      apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
    return {
      "Content-Type": "application/json",
      ...headers,
    };
  }

  async getUserAuth(params: {
    apiKey: string;
    entityId: string;
    uniqueUserId?: string;
    uniqueEmailId?: string;
    userId?: string;
    token?: string;
    userName?: string;
    userImageUrl?: string;
    entityAuthenticationToken?: string;
  }): Promise<UserAuth | null> {
    try {
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId === params.uniqueUserId ||
          externalUserId === params.uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken,
        };
      }

      if (params.uniqueUserId || params.uniqueEmailId) {
        const headers = {
          apiKey: params.apiKey,
          ...(params.token && { token: params.token }),
          ...(params.userId && { userId: params.userId }),
          ...(!!params.entityAuthenticationToken && { "entity-authentication-token": params.entityAuthenticationToken }),
        };

        const body = {
          externalUserId: params.uniqueUserId,
          entityId: params.entityId,
          email: params.uniqueEmailId,
          name: params.userName,
          imageUrl: params.userImageUrl,
          ignoreMAU: true,
        };

        const response = await axios.post(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );

        const { userId, token } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set(
          "externalUserId",
          params.uniqueUserId || params.uniqueEmailId,
          { path: "/", expires }
        );
        this.cookies.set("questUserId", userId, { path: "/", expires });
        this.cookies.set("questUserToken", token, { path: "/", expires });

        return { userId, token };
      }

      return params.userId && params.token
        ? { userId: params.userId, token: params.token }
        : null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async getXp(userId: string, headers: ApiHeaders, entityId: string): Promise<number | null> {
    try {
      const request = `${this.BACKEND_URL}api/entities/${entityId}/users/${userId}/xp`;
      const response = await axios.get(request, {
        headers: this.getAxiosHeaders(headers),
      });
      return response?.data?.data || null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async getUserDetails(
    userId: string,
    headers: ApiHeaders
  ): Promise<UserDetails | null> {
    try {
      const request = `${this.BACKEND_URL}api/users/${userId}`;
      const response = await axios.get(request, {
        headers: this.getAxiosHeaders(headers),
      });
      return {
        userName: response?.data?.data?.name,
        userImage: response?.data?.data?.imageUrl,
      };
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async getLeaderboard(
    entityId: string,
    headers: ApiHeaders,
    userLimit?: number
  ): Promise<LeaderboardResponse | null> {
    try {
      const response = await axios.get<LeaderboardResponse>(
        `${this.BACKEND_URL}api/entities/${entityId}/xp-leaderboard?streak=default_metric&limit=${userLimit}`,
        {
          headers: this.getAxiosHeaders(headers),
        }
      );
      return response.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async getLeaderBoardCampaignResults(
    entityId: string,
    campaignId: string,
    headers: ApiHeaders,
    variation?: string
  ): Promise<LeaderboardCampaignResponse> {
    try {
      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const request = `${
        this.BACKEND_URL
      }api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`;

      const response = await axios.get<ApiCampaignResponse<any>>(request, {
        headers: this.getAxiosHeaders(headers),
      });

      if (response.data?.data?.sdkConfig) {
        return {
          questThemeData:
            response?.data?.data?.sdkConfig?.uiProps?.questThemeData,
          brandTheme: response?.data?.data?.sdkConfig?.uiProps?.BrandTheme,
          showFooter: response?.data?.data?.sdkConfig?.uiProps?.showFooter,
        };
      }

      return {
        questThemeData: undefined,
        brandTheme : undefined,
        showFooter: true,
      };
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return {
        questThemeData: undefined,
        brandTheme : undefined,
        showFooter: true,
      };
    }
  }
}

export default LeaderBoardApi;
